import { motion } from 'framer-motion';
import { Calendar, Download, MapPin, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { Card } from '../ui';
import Input from '../ui/input';
import { productionApi } from './productionApi';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const ProductionView = () => {
  // Log screen view
  useScreenLogging('ProductionView', {
    component: 'ProductionView',
    screenType: 'production',
  });

  const navigate = useNavigate();
  const { openOverlay } = useOverlay();
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedDate, setSelectedDate] = useState('');
  const [sites, setSites] = useState([]);
  const [productionRuns, setProductionRuns] = useState([]);
  const [isLoadingSites, setIsLoadingSites] = useState(true);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]); // Cache templates for name lookup

  // Fetch sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsLoadingSites(true);
      try {
        const sitesData = await productionApi.getSites();
        const sitesList = Array.isArray(sitesData)
          ? sitesData
          : sitesData?.sites || sitesData?.data || [];
        
        // Sort sites alphabetically by name
        const sortedSites = [...sitesList].sort((a, b) => {
          const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
          const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setSites(sortedSites);
        // Don't auto-select - let user select from dropdown
      } catch (error) {
        console.error('Error fetching sites:', error);
        setError('Failed to load sites. Please refresh the page.');
      } finally {
        setIsLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  // Fetch production runs when site or month changes
  useEffect(() => {
    const fetchProductionRuns = async () => {
      if (!selectedSite) {
        setProductionRuns([]);
        return;
      }

      setIsLoadingRuns(true);
      setError('');

      try {
        const result = await productionApi.getProductionRuns({
          siteId: selectedSite.site_id || selectedSite.id,
          month: selectedMonth,
          limit: 100,
        });

        if (result.success) {
          // Filter by selected date if provided
          let filtered = result.runs || [];

          console.log('ProductionView: Raw runs from API:', filtered);
          if (filtered.length > 0) {
            console.log('ProductionView: First run structure:', filtered[0]);
          }

          if (selectedDate) {
            filtered = filtered.filter(run => {
              const runDate = run.production_date || run.event_date;
              return runDate && runDate.startsWith(selectedDate);
            });
          }

          // Enrich runs with template and site names
          const enrichedRuns = filtered.map(run => enrichRunData(run));
          console.log('ProductionView: Enriched runs:', enrichedRuns);

          setProductionRuns(enrichedRuns);
        } else {
          setError(result.message || 'Failed to load production runs');
          setProductionRuns([]);
        }
      } catch (error) {
        console.error('Error fetching production runs:', error);
        setError('Failed to load production runs. Please try again.');
        setProductionRuns([]);
      } finally {
        setIsLoadingRuns(false);
      }
    };

    fetchProductionRuns();
  }, [selectedSite, selectedMonth, selectedDate]);

  // Helper function to get template name from template ID
  const getTemplateName = templateId => {
    if (!templateId) return null;
    const template = templates.find(
      t => (t.production_template_id || t.template_id || t.id) === templateId
    );
    return template?.template_name || template?.name || null;
  };

  // Helper function to get site name from site ID
  const getSiteName = siteId => {
    if (!siteId) return null;
    const site = sites.find(s => (s.site_id || s.id) === siteId);
    return site?.site_name || site?.name || null;
  };

  // Enrich run data with template and site names
  const enrichRunData = run => {
    const templateId = run.production_template_id || run.template_id;
    const siteId = run.site_id;

    return {
      ...run,
      // API returns production_name which is the template name
      template_name: run.production_name || run.template_name || getTemplateName(templateId) || null,
      site_name: run.site_name || getSiteName(siteId) || null,
      // API uses batch_reference instead of batch_id
      batch_id: run.batch_reference || run.batch_id || run.batchId || null,
      // API uses production_id as the ID
      id: run.production_id || run.id || null,
    };
  };

  const handleDownload = run => {
    const csvData = [
      [
        'Name',
        'Type',
        'Quantity',
        'Raw Material Cost',
        'Batch ID',
        'Date',
        'Site',
      ],
      [
        run.production_name || run.template_name || run.name || 'Unknown Template',
        run.production_name || run.template_name || run.type || 'N/A',
        run.units_produced || run.quantity || 'N/A',
        run.raw_material_cost || run.rawMaterialCost || 0,
        run.batch_reference || run.batch_id || run.batchId || 'N/A',
        run.production_date || run.date || 'N/A',
        run.site_name || run.siteName || 'N/A',
      ],
    ]
      .map(csvRow => csvRow.join(','))
      .join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `production-${run.batch_reference || run.batch_id || run.batchId || 'unknown'}-${
      run.production_date || run.date || 'unknown'
    }.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };


  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };



  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header with Title and Create Button */}
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={typography.h2} style={{ color: '#000000' }}>
                Production
              </h1>
              <p className="mt-2 text-gray-600">
                View and manage production entries
              </p>
            </div>
            <button
              onClick={() => navigate('/onboarding-production/create')}
              className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              <Plus className="w-4 h-4" />
              Production Entry
            </button>
          </div>

          <div className="rounded-3xl">
            {/* Site and Month Selection */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
              <div className="min-w-[200px]">
                <label
                  htmlFor="production-site"
                  className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
                >
                  <MapPin className="w-4 h-4" />
                  Select Site
                </label>
                <select
                  id="production-site"
                  value={
                    selectedSite ? selectedSite.site_id || selectedSite.id : ''
                  }
                  onChange={e => {
                    const siteId = e.target.value;
                    const site = sites.find(
                      s => (s.site_id || s.id) === siteId
                    );
                    setSelectedSite(site || null);
                  }}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  required
                >
                  <option value="">
                    {isLoadingSites ? 'Loading sites...' : 'Select a site'}
                  </option>
                  {/* alphabetical order */}
                  {Array.isArray(sites) && sites.map((site) => (
                    <option key={site.site_id || site.id} value={site.site_id || site.id}>
                      {site.site_name || site.name || 'Unknown Site'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[200px]">
                <label
                  htmlFor="production-month"
                  className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
                >
                  <Calendar className="w-4 h-4" />
                  Select Month
                </label>
                <Input
                  id="production-month"
                  type="month"
                  value={selectedMonth}
                  onChange={event => setSelectedMonth(event.target.value)}
                  className="w-full h-12 rounded-2xl border bg-white pr-4 text-sm focus:outline-none focus:ring-2 border-gray-200 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Production Cards - Grouped by Date */}
            <div className="space-y-4">
              {error && (
                <Card.Light className="p-4 bg-red-50 border border-red-200">
                  <p className="text-sm text-red-800">{error}</p>
                </Card.Light>
              )}
              {isLoadingRuns ? (
                <Card.Light className="p-8 text-center">
                  <p className="text-gray-500 text-lg">
                    Loading production runs...
                  </p>
                </Card.Light>
              ) : productionRuns.length > 0 ? (
                <>
                  {productionRuns.map((run, index) => (
                    <motion.div
                      key={run.production_id || run.id || index}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      <Card.Light
                        className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => openOverlay('productionDetails', {
                          productionRun: run,
                          onDownload: handleDownload
                        })}
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold">
                                {index + 1}
                              </div>
                              <h3 className="text-xl font-semibold text-gray-800">
                                {run.production_name ||
                                  run.template_name ||
                                  run.name ||
                                  'Unknown Template'}
                              </h3>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div>
                                <span className="text-gray-600">Type: </span>
                                <span className="font-medium">
                                  {run.production_name || run.template_name || run.type || 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">
                                  Quantity:{' '}
                                </span>
                                <span className="font-medium">
                                  {run.units_produced || run.quantity || 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Cost: </span>
                                <span className="font-medium text-green-600">
                                  ₹
                                  {(
                                    run.raw_material_cost ||
                                    run.rawMaterialCost ||
                                    0
                                  ).toFixed(2)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">
                                  Batch ID:{' '}
                                </span>
                                <span className="font-medium">
                                  {run.batch_reference || run.batch_id || run.batchId || 'N/A'}
                                </span>
                              </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-3 text-sm">
                              <div>
                                <span className="text-gray-600">Date: </span>
                                <span className="font-medium">
                                  {new Date(
                                    run.production_date || run.date
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Site: </span>
                                <span className="font-medium">
                                  {run.site_name || run.siteName || 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                handleDownload(run);
                              }}
                              className="w-10 h-10 p-2 bg-blue-700 rounded flex justify-center items-center gap-2.5 cursor-pointer hover:bg-blue-800 transition-colors"
                              title="Download"
                            >
                              <Download
                                className="w-6 h-6 text-white stroke-white"
                                strokeWidth={2}
                              />
                            </button>
                          </div>
                        </div>
                      </Card.Light>
                    </motion.div>
                  ))}
                </>
              ) : (
                <Card.Light className="p-8 text-center">
                  <p className="text-gray-500 text-lg">
                    {!selectedSite
                      ? 'Please select a site to view production entries.'
                      : 'No production entries found for the selected filters.'}
                  </p>
                </Card.Light>
              )}
            </div>

          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProductionView;
